import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import { getSEOConfig, updateSEOTags } from '../../i18n/utils/seo';
import type { SupportedLanguage } from '../../i18n/utils/language';

type DiscordFormat = 't' | 'T' | 'd' | 'D' | 'f' | 'F' | 'R';

export default function DiscordTimestamp() {
  const { t, i18n } = useTranslation(['discord', 'common']);

  // State
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    now.setSeconds(0, 0); // Round to minute
    return now;
  });
  const [selectedFormat, setSelectedFormat] = useState<DiscordFormat>('F');
  const [copySuccess, setCopySuccess] = useState('');

  // Update SEO
  useEffect(() => {
    const currentLang = i18n.language as SupportedLanguage;
    const seoConfig = getSEOConfig('discordTimestamp', currentLang);
    updateSEOTags(seoConfig, currentLang);
    document.documentElement.lang = currentLang;
  }, [i18n.language]);

  // Generate Discord timestamp code
  const generateDiscordCode = (date: Date, format: DiscordFormat): string => {
    const timestamp = Math.floor(date.getTime() / 1000);
    return `<t:${timestamp}:${format}>`;
  };

  // Format preview text
  const formatPreviewText = (date: Date, format: DiscordFormat): string => {
    const options: Intl.DateTimeFormatOptions = {};
    const locale = i18n.language;

    switch (format) {
      case 't': // Short Time
        return date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
      case 'T': // Long Time
        return date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', second: '2-digit' });
      case 'd': // Short Date
        return date.toLocaleDateString(locale, { year: 'numeric', month: 'numeric', day: 'numeric' });
      case 'D': // Long Date
        return date.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
      case 'f': // Short Date/Time
        return date.toLocaleString(locale, { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' });
      case 'F': // Long Date/Time
        return date.toLocaleString(locale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' });
      case 'R': // Relative Time
        const now = new Date();
        const diffMs = date.getTime() - now.getTime();
        const diffSecs = Math.floor(Math.abs(diffMs) / 1000);
        const diffMins = Math.floor(diffSecs / 60);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffMs < 0) {
          if (diffDays > 0) return `${diffDays} days ago`;
          if (diffHours > 0) return `${diffHours} hours ago`;
          if (diffMins > 0) return `${diffMins} minutes ago`;
          return `${diffSecs} seconds ago`;
        } else {
          if (diffDays > 0) return `in ${diffDays} days`;
          if (diffHours > 0) return `in ${diffHours} hours`;
          if (diffMins > 0) return `in ${diffMins} minutes`;
          return `in ${diffSecs} seconds`;
        }
    }
  };

  const currentCode = generateDiscordCode(selectedDate, selectedFormat);
  const previewText = formatPreviewText(selectedDate, selectedFormat);

  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(text);
      setTimeout(() => setCopySuccess(''), 2000);
    });
  };

  // Handle datetime input
  const handleDateTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = new Date(e.target.value);
    if (!isNaN(newDate.getTime())) {
      setSelectedDate(newDate);
    }
  };

  // Format datetime for input
  const formatDateTimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const formats: Array<{ value: DiscordFormat; label: string; example: string }> = [
    { value: 't', label: t('page.format.t'), example: t('page.format.t.example') },
    { value: 'T', label: t('page.format.T'), example: t('page.format.T.example') },
    { value: 'd', label: t('page.format.d'), example: t('page.format.d.example') },
    { value: 'D', label: t('page.format.D'), example: t('page.format.D.example') },
    { value: 'f', label: t('page.format.f'), example: t('page.format.f.example') },
    { value: 'F', label: t('page.format.F'), example: t('page.format.F.example') },
    { value: 'R', label: t('page.format.R'), example: t('page.format.R.example') },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-6xl mx-auto px-4 py-4 md:py-8">
        {/* Page Header */}
        <div className="text-center mb-6 md:mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            {t('page.mainTitleSEO')}
          </h1>
          <p className="text-lg text-gray-600">{t('page.subtitle')}</p>
        </div>

        {/* Generator Tool */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-6">
            {t('page.generatorTitle')}
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('page.selectDateTime')}
                </label>
                <input
                  type="datetime-local"
                  value={formatDateTimeLocal(selectedDate)}
                  onChange={handleDateTimeChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('page.chooseFormat')}
                </label>
                <select
                  value={selectedFormat}
                  onChange={(e) => setSelectedFormat(e.target.value as DiscordFormat)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all"
                >
                  {formats.map((fmt) => (
                    <option key={fmt.value} value={fmt.value}>
                      {fmt.label} - {fmt.example}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Output Section */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('page.generatedCode')}
                </label>
                <div className="relative">
                  <code className="block w-full px-4 py-3 bg-gray-900 text-green-400 rounded-lg font-mono text-sm break-all">
                    {currentCode}
                  </code>
                  <button
                    onClick={() => copyToClipboard(currentCode)}
                    className="absolute top-2 right-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1.5 rounded text-sm transition-colors"
                  >
                    {copySuccess === currentCode ? t('common:button.copied') : t('page.copyCode')}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('page.preview')}
                </label>
                <div className="w-full px-4 py-3 bg-indigo-50 border-2 border-indigo-200 rounded-lg text-indigo-900 font-medium">
                  {previewText}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Format Reference Table */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-6">
            {t('page.formatReference')}
          </h2>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('page.formatCode')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('page.style')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('page.exampleOutput')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('page.useCase')}</th>
                </tr>
              </thead>
              <tbody>
                {formats.map((fmt, idx) => (
                  <tr key={fmt.value} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3">
                      <code className="text-sm bg-gray-800 text-green-400 px-2 py-1 rounded">
                        &lt;t:1234567890:{fmt.value}&gt;
                      </code>
                    </td>
                    <td className="px-4 py-3 font-medium">{fmt.label}</td>
                    <td className="px-4 py-3 text-gray-600">{fmt.example}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{t(`page.useCase.${fmt.value}`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* How to Use */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-6">
            {t('page.howToUse')}
          </h2>

          <div className="space-y-4">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-start space-x-4">
                <div className="w-8 h-8 flex items-center justify-center bg-indigo-100 text-indigo-600 rounded-full text-sm font-bold flex-shrink-0">
                  {step}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">{t(`page.step${step}`)}</h3>
                  <p className="text-gray-600">{t(`page.step${step}Desc`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Common Use Cases */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-6">
            {t('page.commonUseCases')}
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((num) => (
              <div key={num} className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-5 border border-indigo-200">
                <h3 className="font-semibold text-gray-800 mb-2">{t(`page.useCase${num}.title`)}</h3>
                <p className="text-sm text-gray-600">{t(`page.useCase${num}.desc`)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-6">
            {t('page.faq')}
          </h2>

          <div className="space-y-4">
            {[1, 2, 3, 4, 5, 6].map((num) => (
              <details key={num} className="group">
                <summary className="flex justify-between items-center cursor-pointer list-none p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
                  <span className="font-semibold text-gray-800">{t(`page.faq.q${num}`)}</span>
                  <span className="transition group-open:rotate-180">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                </summary>
                <div className="px-4 py-3 text-gray-600">
                  {t(`page.faq.a${num}`)}
                </div>
              </details>
            ))}
          </div>
        </div>

        {/* Related Tools */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-4">
            {t('page.relatedTools')}
          </h2>

          <div className="space-y-4">
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-5">
              <h3 className="font-semibold text-gray-800 mb-2">{t('page.needUnixTimestamp')}</h3>
              <p className="text-gray-600 mb-3">{t('page.needUnixTimestampDesc')}</p>
              <a
                href="/timestamp-converter"
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg transition-colors"
              >
                {t('common:nav.timestamp')} →
              </a>
            </div>

            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-5">
              <h3 className="font-semibold text-gray-800 mb-2">{t('page.checkYouTubeTool')}</h3>
              <a
                href="/youtube-timestamp-link"
                className="inline-block bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-lg transition-colors"
              >
                YouTube Timestamp Link →
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
